/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    COMPREHENSIVE_OPTIONAL_FIELDS_FIX
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Updated imports to point to refactored UserProfileForm and CompanyProfileForm.
 *
 * Related Screens / Modules:
 * - client/src/components/Setup.tsx
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { faBuilding, faUser, faCheck, faBell } from "@fortawesome/free-solid-svg-icons";

// Import the setup components
import CompanyProfileForm from "./setup/company-profile/CompanyProfileForm";
import UserProfileForm from "./setup/user-profile/UserProfileForm";
import ReviewProfileForm from "./setup/review-profile/ReviewProfileForm";
import AlertsForm from "./setup/AlertsForm";
import { User } from "../interfaces/User";
import { getUserProfile, saveUserProfile } from "../services/usersService";

type SetupSection = "company" | "profile" | "review" | "alerts";

interface SetupItem {
  id: SetupSection;
  label: string;
  icon: any;
  component: React.ComponentType;
  isSeparated?: boolean;
}

// Context to share profile state between setup forms
interface SetupContextValue {
  profile: Partial<User> | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  save: (changes: Partial<User>, options?: { draft?: boolean }) => Promise<void>;
  isDirty: boolean;
  setDirty: (dirty: boolean) => void;
}

export const SetupContext = createContext<SetupContextValue | undefined>(undefined);

export const useSetup = (): SetupContextValue => {
  const ctx = useContext(SetupContext);
  if (!ctx) throw new Error("useSetup must be used within SetupContextProvider");
  return ctx;
};

export const SetupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<Partial<User> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setDirty] = useState<boolean>(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getUserProfile();
      setProfile(res.data as User);
    } catch (e: any) {
      // Ensure error is always a string
      let errorMessage = "Failed to load profile";
      if (e?.response?.data?.error) {
        // Handle {error: "message"} format from backend
        errorMessage =
          typeof e.response.data.error === "string"
            ? e.response.data.error
            : JSON.stringify(e.response.data.error);
      } else if (e?.response?.data) {
        errorMessage =
          typeof e.response.data === "string" ? e.response.data : JSON.stringify(e.response.data);
      } else if (e?.message) {
        errorMessage = typeof e.message === "string" ? e.message : JSON.stringify(e.message);
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(
    async (changes: Partial<User>, options?: { draft?: boolean }) => {
      await saveUserProfile(changes, options);
      await refresh();
      window.dispatchEvent(new CustomEvent("userProfileSaved"));
    },
    [refresh],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setDirtyCallback = useCallback((dirty: boolean) => {
    setDirty(dirty);
  }, []);

  const value = useMemo(
    () => ({ profile, loading, error, refresh, save, isDirty, setDirty: setDirtyCallback }),
    [profile, loading, error, isDirty, save, refresh, setDirtyCallback],
  );

  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
};

const SetupContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get("section") as SetupSection | null;
  const [activeSection, setActiveSection] = useState<SetupSection>(
    (sectionParam && ["company", "profile", "review", "alerts"].includes(sectionParam)
      ? sectionParam
      : "company") as SetupSection,
  );

  // Sync activeSection with URL parameter
  useEffect(() => {
    if (sectionParam && ["company", "profile", "review", "alerts"].includes(sectionParam)) {
      setActiveSection(sectionParam as SetupSection);
    }
  }, [sectionParam]);

  const setupItems: SetupItem[] = [
    {
      id: "company",
      label: "Company Profile",
      icon: faBuilding,
      component: CompanyProfileForm,
    },
    {
      id: "profile",
      label: "My profile",
      icon: faUser,
      component: UserProfileForm,
    },
    {
      id: "review",
      label: "Review",
      icon: faCheck,
      component: ReviewProfileForm,
    },
    {
      id: "alerts",
      label: "Broadcast Alerts",
      icon: faBell,
      component: AlertsForm,
      isSeparated: true, // This will add a separator line above this item
    },
  ];

  const ActiveComponent =
    setupItems.find((item) => item.id === activeSection)?.component || CompanyProfileForm;

  return (
    <Container fluid className="setup-page" style={{ maxWidth: "100%", width: "100%" }}>
      {/* Top Banner */}
      {/* <SetupTopBanner activeSection={activeSection} /> */}

      <Row className="h-100">
        {/* Content Area - Full Width */}
        <Col xs={12} className="setup-content" style={{ minWidth: 0 }}>
          {/* <div className="setup-content-area"> */}
          {/* Render the active component */}
          <ActiveComponent />
          {/* </div> */}
        </Col>
      </Row>
    </Container>
  );
};

const Setup: React.FC = () => {
  return (
    <SetupProvider>
      <SetupContent />
    </SetupProvider>
  );
};

export default Setup;
